// Quil's expectation checks (see expectations.ts for the mechanism). Two:
//
//  1. The shared "did the core sync path do anything lately" check: main.tsx records
//     the day the app was opened, every successful load / pull / upload drain adds
//     one (sync.ts), and three opened days with no success is the silent failure
//     shape nothing else would report.
//  2. The one from the error-sync design: a work edited over an hour ago whose
//     autosave never reached OneDrive. Nothing throws when that happens (the local
//     Dexie save succeeded, the upload just never landed), so it is read straight
//     from the sync queue: an upload entry that has sat there for over an hour is
//     an autosave that did not complete. Conflicts are excluded (a 412 drops its
//     entry and waits for the writer, by design), as is offline (the pill already
//     says so and the drain resumes on reconnect).
import { db } from './db'
import { runExpectationChecks, syncSilentCheck, type ExpectationCheck } from './expectations'

export const APP_ID = 'quil'
const STALE_UPLOAD_MS = 60 * 60 * 1000

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export async function staleUploads(now: number = Date.now()): Promise<string[]> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return []
  const pending = await db.syncQueue.where('operation').equals('upload').toArray()
  const stale = pending.filter((e) => now - e.createdAt > STALE_UPLOAD_MS && !/^offline:/.test(e.errorMessage ?? ''))
  const workIds = [...new Set(stale.map((e) => e.workId))]
  const titles: string[] = []
  for (const id of workIds) {
    const work = await db.works.get(id)
    titles.push(work?.title || id)
  }
  return titles
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export const staleAutosaveCheck: ExpectationCheck = {
  id: 'autosave-not-uploaded',
  check: async () => {
    const titles = await staleUploads()
    if (titles.length === 0) return null
    return `edits over an hour old have not reached OneDrive for ${titles.length} work${titles.length === 1 ? '' : 's'}: ${titles.slice(0, 5).join(', ')}`
  },
}

const QUIL_CHECKS: ExpectationCheck[] = [syncSilentCheck(APP_ID), staleAutosaveCheck]

/** Run Quil's checks (once a day each). Called after an upload drain has settled. */
export function runQuilChecks(now: number = Date.now()): void {
  runExpectationChecks(APP_ID, QUIL_CHECKS, now)
}
